import React, { useCallback, useEffect, useState } from "react";
import { MessageSquare, Send, Save, Moon, Gauge, Inbox as InboxIcon, Check, X, Bot } from "lucide-react";
import { PageHeader, Button, Input, Select, Textarea, Checkbox, StatusBadge, LoadingBlock, EmptyState, toast } from "@/components/ds";
import { api, formatApiError } from "@/lib/api";

const MODE_LABELS: Record<string, string> = {
  staff_only: "Staff only", ai_draft_only: "AI drafts only",
  ai_auto_faq: "AI auto-answers approved FAQs", ai_faq_qualify: "AI answers FAQs + qualifies leads",
  ai_faq_qualify_book: "AI answers, qualifies + books",
};
const ACTION_TONE: Record<string, "green" | "amber" | "red" | "slate" | "blue"> = {
  sent: "green", drafted: "amber", failed: "red", opted_out: "slate", suppressed: "slate", staff_only: "slate", escalated: "red",
};

export default function SmsAutopilot() {
  const [settings, setSettings] = useState<any>(null);
  const [modes, setModes] = useState<string[]>([]);
  const [drafts, setDrafts] = useState<any[] | null>(null);
  const [from, setFrom] = useState("(312) 555-4321");
  const [body, setBody] = useState("");
  const [failSend, setFailSend] = useState(false);
  const [lastResult, setLastResult] = useState<any>(null);
  const [busy, setBusy] = useState(false);

  const loadDrafts = useCallback(() => api.smsDrafts().then(setDrafts).catch(() => {}), []);
  useEffect(() => {
    api.smsMeta().then((m) => setModes(m.modes)).catch(() => {});
    api.smsSettings().then(setSettings).catch((e) => toast.error(formatApiError(e)));
    loadDrafts();
  }, [loadDrafts]);

  const save = async (patch: any) => {
    try { setSettings(await api.smsUpdateSettings(patch)); toast.success("Settings saved"); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  const sendInbound = async () => {
    if (!body.trim()) return;
    setBusy(true);
    try {
      const r = await api.smsInbound({ from_number: from, body: body.trim(), fail_send: failSend });
      setLastResult(r); setBody("");
      toast.success(`AI ${r.action}`);
      await loadDrafts();
    } catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); }
  };

  const sendDraft = async (id: string) => { try { await api.smsSendDraft(id); toast.success("Draft sent"); await loadDrafts(); } catch (e) { toast.error(formatApiError(e)); } };
  const discardDraft = async (id: string) => { try { await api.smsDiscardDraft(id); toast.success("Draft discarded"); await loadDrafts(); } catch (e) { toast.error(formatApiError(e)); } };

  if (!settings) return <div className="p-2"><LoadingBlock rows={5} /></div>;
  const qh = settings.quiet_hours || {};

  return (
    <div data-testid="page-sms" className="space-y-6">
      <PageHeader title="SMS Autopilot" description="Choose how much the AI handles over text — with quiet hours, rate limits and staff review. (Twilio connects later; inbound is simulated here.)" testid="sms-header" />

      <div className="grid gap-4 lg:grid-cols-2">
        {/* Settings */}
        <div className="rounded-lg border border-slate-200 bg-white p-5" data-testid="sms-settings">
          <div className="mb-3 flex items-center gap-2 text-sm font-semibold text-slate-800"><Bot className="h-4 w-4 text-blue-500" /> Autonomy mode</div>
          <Select value={settings.mode} onChange={(m) => save({ mode: m })} testid="sms-mode-select"
            options={modes.map((m) => ({ value: m, label: MODE_LABELS[m] ?? m }))} />
          <p className="mt-1 text-xs text-slate-400">Controls what the AI may do automatically for inbound texts.</p>

          <div className="mt-5 flex items-center justify-between border-t border-slate-100 pt-4">
            <div className="flex items-center gap-2"><Moon className="h-4 w-4 text-slate-400" /><span className="text-sm font-medium text-slate-700">Quiet hours</span></div>
            <Checkbox checked={!!qh.enabled} onChange={(v: boolean) => save({ quiet_hours: { ...qh, enabled: v } })} testid="sms-quiet-toggle" />
          </div>
          {qh.enabled && (
            <div className="mt-2 flex items-center gap-2" data-testid="sms-quiet-hours">
              <Input type="number" min={0} max={23} value={qh.start_hour} onChange={(e) => save({ quiet_hours: { ...qh, start_hour: Number(e.target.value) } })} className="w-20" data-testid="sms-quiet-start" />
              <span className="text-xs text-slate-400">to</span>
              <Input type="number" min={0} max={23} value={qh.end_hour} onChange={(e) => save({ quiet_hours: { ...qh, end_hour: Number(e.target.value) } })} className="w-20" data-testid="sms-quiet-end" />
              <span className="text-xs text-slate-400">(no auto-sends during this window — drafts instead)</span>
            </div>
          )}

          <div className="mt-5 border-t border-slate-100 pt-4">
            <div className="mb-1 flex items-center gap-2"><Gauge className="h-4 w-4 text-slate-400" /><span className="text-sm font-medium text-slate-700">Auto-send limit</span></div>
            <div className="flex items-center gap-2">
              <Input type="number" min={1} max={100} value={settings.rate_limit_per_hour} onChange={(e) => setSettings({ ...settings, rate_limit_per_hour: Number(e.target.value) })} className="w-24" data-testid="sms-rate-input" />
              <span className="text-xs text-slate-400">auto-sends per contact / hour</span>
              <Button size="sm" variant="outline" onClick={() => save({ rate_limit_per_hour: settings.rate_limit_per_hour })} data-testid="sms-rate-save"><Save className="h-4 w-4" /> Save</Button>
            </div>
          </div>
        </div>

        {/* Inbound simulator */}
        <div className="rounded-lg border border-slate-200 bg-white p-5" data-testid="sms-simulator">
          <div className="mb-3 flex items-center gap-2 text-sm font-semibold text-slate-800"><MessageSquare className="h-4 w-4 text-emerald-500" /> Simulate an inbound text</div>
          <Input value={from} onChange={(e) => setFrom(e.target.value)} placeholder="From number" className="mb-2" data-testid="sms-from" />
          <Textarea value={body} onChange={(e) => setBody(e.target.value)} rows={3} placeholder="Customer text (try 'How much is a tune-up?', 'STOP', or 'I need a person')" data-testid="sms-body" />
          <div className="mt-2 flex items-center justify-between">
            <label className="flex items-center gap-2 text-xs text-slate-500"><Checkbox checked={failSend} onChange={(v: boolean) => setFailSend(v)} testid="sms-fail-toggle" /> Simulate send failure</label>
            <Button onClick={sendInbound} disabled={busy || !body.trim()} data-testid="sms-send-inbound"><Send className="h-4 w-4" /> {busy ? "Processing…" : "Receive text"}</Button>
          </div>
          {lastResult && (
            <div className="mt-3 rounded-md border border-slate-100 bg-slate-50 p-3" data-testid="sms-last-result">
              <div className="flex items-center gap-2"><span className="text-xs text-slate-400">Result</span><StatusBadge tone={ACTION_TONE[lastResult.action] ?? "slate"} data-testid="sms-result-action">{lastResult.action}</StatusBadge>{lastResult.intent && <StatusBadge tone="blue">{lastResult.intent}</StatusBadge>}{lastResult.reason && <span className="text-[11px] text-slate-400">({lastResult.reason})</span>}</div>
              {lastResult.response && <p className="mt-1.5 text-sm text-slate-700">{lastResult.response}</p>}
            </div>
          )}
        </div>
      </div>

      {/* Drafts queue */}
      <div className="rounded-lg border border-slate-200 bg-white p-5" data-testid="sms-drafts">
        <div className="mb-3 flex items-center gap-2 text-sm font-semibold text-slate-800"><InboxIcon className="h-4 w-4 text-indigo-500" /> Staff draft queue</div>
        {drafts === null ? <LoadingBlock rows={2} /> : drafts.length === 0 ? (
          <EmptyState icon={InboxIcon} title="No pending drafts" description="AI-drafted replies awaiting review will appear here." />
        ) : (
          <div className="space-y-2">
            {drafts.map((d) => (
              <div key={d.id} className="flex items-start justify-between gap-3 rounded-md border border-slate-100 p-3" data-testid={`draft-${d.id}`}>
                <div className="min-w-0">
                  <p className="text-xs font-medium text-slate-500">{d.conversation?.customer_name ?? "Customer"} · {d.conversation?.customer_phone ?? ""}{d.metadata?.draft_reason ? ` · ${d.metadata.draft_reason}` : ""}</p>
                  <p className="mt-0.5 text-sm text-slate-800">{d.body}</p>
                </div>
                <div className="flex shrink-0 gap-1">
                  <Button size="sm" onClick={() => sendDraft(d.id)} data-testid={`draft-send-${d.id}`}><Check className="h-4 w-4" /> Send</Button>
                  <Button size="sm" variant="outline" onClick={() => discardDraft(d.id)} data-testid={`draft-discard-${d.id}`}><X className="h-4 w-4" /></Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
